import { matchRoutes, type RouteObject } from "react-router";

/**
 * Re-targets the current page at a new domain/company scope: keeps the
 * current top-level feature if the router actually has a route for it at the
 * new scope (dropping any deeper, resource-specific sub-path — those ids
 * don't carry over to a new scope), otherwise falls back to the new scope's
 * own root. `routeObjects` and `gatedFeatureSegments` come from router.tsx
 * (via `useRouteTree()`) rather than being imported here, so this module
 * can't drift from what router.tsx actually mounts and doesn't create an
 * import cycle back into router.tsx.
 */
export const buildScopedNavigationPath = ({
	pathname,
	companyDomainId,
	legalEntityId,
	routeObjects,
	gatedFeatureSegments,
}: {
	pathname: string;
	companyDomainId: string;
	legalEntityId?: string;
	routeObjects: RouteObject[];
	gatedFeatureSegments: ReadonlySet<string>;
}) => {
	const base = legalEntityId
		? `/company-domains/${companyDomainId}/legal-entities/${legalEntityId}`
		: `/company-domains/${companyDomainId}`;

	// The negative lookahead keeps "hierarchy" from being consumed as if it
	// were a legal-entity id — it's a domain-wide sibling tab of legal-entities
	// (GroupHierarchyCard), not a resource under a specific entity.
	const rest = pathname.replace(
		/^\/company-domains\/[^/]+(?:\/legal-entities\/(?!hierarchy(?:\/|$))[^/]+)?\/?/,
		"",
	);
	const rawSegment = rest.split("/")[0];
	// A plan detail page belongs to the Planning feature.
	const featureSegment = rawSegment === "plan" ? "planning" : rawSegment;
	if (!featureSegment || gatedFeatureSegments.has(featureSegment)) {
		return base;
	}

	const candidate = `${base}/${featureSegment}`;
	const matches = matchRoutes(routeObjects, candidate);
	// Root route tree ends in a catch-all, so an unmatched path resolves here too.
	const leaf = matches?.at(-1)?.route;
	// A parent route can match with no element — require one to avoid a blank page.
	const isRealMatch =
		leaf?.path !== "*" &&
		Boolean(leaf?.element ?? leaf?.lazy ?? leaf?.Component);
	return isRealMatch ? candidate : base;
};
